import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import {
  Assessment_Activity_Status_Enum,
  Assessment_Activity_Type_Enum,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import AssessmentActivityForm from 'src/pages/assessments/forms/assessment-activity-form';
import type { AssessmentActivityFormDataFields } from 'src/pages/assessments/forms/assessment-activity-form/assessmentActivitySchema';

import { useInsertAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useInsertAssessmentActivityWithLinkedItems';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';

import type { AssessmentTypeEnum } from '../../../../types';
import { useAssessmentTypeConfig } from '../../../../useAssessmentTypeConfig';

interface Props {
  assessmentMode: AssessmentTypeEnum;
}

const AssessmentActivityCreateTab: FC<Props> = ({ assessmentMode }) => {
  const navigate = useNavigate();
  const assessmentId = useGetGuidParam('assessmentId');
  const { updateFiles } = useFileUpdate();
  const { insertAssessmentActivity } =
    useInsertAssessmentActivityWithLinkedItems();
  const {
    routing: { activityRegisterUrl },
  } = useAssessmentTypeConfig(assessmentMode);

  const onSave = async (variables: AssessmentActivityFormDataFields) => {
    const { files } = variables;
    const result = await insertAssessmentActivity({
      ...variables,
      ParentId: assessmentId,
      LinkedItemIds: variables.LinkedItemIds.map((c) => c.Id),
      CustomAttributeData: variables.CustomAttributeData || undefined,
      AssignedUser: variables.AssignedUser?.value,
      OwnerUserIds: [],
      OwnerGroupIds: [],
      Status: variables.Status ?? Assessment_Activity_Status_Enum.Notstarted,
      ActivityType:
        variables.ActivityType ?? Assessment_Activity_Type_Enum.Task,
    });
    if (!result?.insertAssessmentActivityWithLinkedItems?.Id) {
      throw new Error('Assessment Activity id is missing');
    }
    await updateFiles({
      parentId: result.insertAssessmentActivityWithLinkedItems.Id,
      parentType: Parent_Type_Enum.AssessmentActivity,
      selectedFiles: files,
      originalFiles: [],
    });
    if (result?.insertAssessmentActivityWithLinkedItems?.Id) {
      navigate(activityRegisterUrl(assessmentId));
    }
  };

  const onDismiss = (saved?: boolean) => {
    if (!saved) {
      navigate(activityRegisterUrl(assessmentId));
    }
  };

  return (
    <AssessmentActivityForm
      onSave={onSave}
      onDismiss={onDismiss}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default AssessmentActivityCreateTab;
